import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Calculator, ArrowRight, ChatCircle, FilmStrip, MapPin, Briefcase, Lightning } from "@phosphor-icons/react";
import api from "../lib/api";
import { flag, countryName } from "../lib/format";

/* Activity profiles — calibrated to typical usage patterns
 * Returns MB/day at "normal" use intensity. */
const ACTIVITIES = [
  { id: "social", label: "calc.actSocial", icon: ChatCircle, mbPerDay: 1350, color: "bg-cobalt-50 text-cobalt-600", emoji: "💬" },
  { id: "stream", label: "calc.actStream", icon: FilmStrip, mbPerDay: 3000, color: "bg-coral-soft/60 text-coral-deep", emoji: "🎬" },
  { id: "maps",   label: "calc.actMaps",   icon: MapPin,    mbPerDay: 160,  color: "bg-mint-soft text-mint", emoji: "🗺️" },
  { id: "work",   label: "calc.actWork",   icon: Briefcase, mbPerDay: 750,  color: "bg-sun-soft text-amber-700", emoji: "💼" },
];

const TRIP_LENGTHS = [
  { id: "1-3",   label: "calc.trip1",   days: 2  },
  { id: "4-6",   label: "calc.trip2",   days: 5  },
  { id: "7-15",  label: "calc.trip3",   days: 10 },
  { id: "16-30", label: "calc.trip4",   days: 20 },
  { id: "30+",   label: "calc.trip5",   days: 35 },
];

/* compact = home version, full = standalone page */
export default function DataCalculator({ compact = false }) {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [country, setCountry] = useState(null);
  const [trip, setTrip] = useState(null);
  const [acts, setActs] = useState({});
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    api.get("/plans/countries").then(({ data }) => setCountries(data));
  }, []);

  const totalMb = useMemo(() => {
    if (!trip) return 0;
    const dailyMb = Object.entries(acts)
      .filter(([, on]) => on)
      .reduce((sum, [id]) => sum + (ACTIVITIES.find((a) => a.id === id)?.mbPerDay || 0), 0);
    return Math.ceil(dailyMb * trip.days);
  }, [trip, acts]);

  const recommendedGb = useMemo(() => {
    if (totalMb <= 0) return 0;
    const gb = totalMb / 1024;
    if (gb <= 1) return 1;
    if (gb <= 3) return 3;
    if (gb <= 5) return 5;
    if (gb <= 10) return 10;
    if (gb <= 20) return 20;
    return Math.ceil(gb / 10) * 10;
  }, [totalMb]);

  const toggleAct = (id) => setActs((s) => ({ ...s, [id]: !s[id] }));

  const reset = () => { setStep(0); setCountry(null); setTrip(null); setActs({}); };

  const goPlans = () => {
    if (country) nav(`/plans?country=${country.country_iso2}`);
    else nav("/plans");
  };

  const Container = ({ children }) => (
    <div className={`card-soft ${compact ? "!rounded-3xl p-6 lg:p-8" : "p-8 lg:p-10"}`} data-testid="data-calculator">
      {children}
    </div>
  );

  return (
    <Container>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-2xl bg-cobalt-500 text-white flex items-center justify-center">
          <Calculator size={22} weight="bold" />
        </div>
        <div>
          <div className="overline">{t("calc.eyebrow")}</div>
          <div className="font-display font-bold text-2xl tracking-tight">{t("calc.title")}</div>
        </div>
      </div>

      {/* Progress dots */}
      <div className="flex items-center gap-2 mb-6">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full transition-all ${i <= step ? "bg-cobalt-500" : "bg-ink-200"}`} />
        ))}
      </div>

      {/* STEP 0 — Country */}
      {step === 0 && (
        <div data-testid="calc-step-country">
          <label className="block">
            <span className="text-sm font-semibold">{t("calc.q1")}</span>
            <select
              className="input-soft mt-3"
              value={country?.country_iso2 || ""}
              onChange={(e) => setCountry(countries.find((c) => c.country_iso2 === e.target.value) || null)}
              data-testid="calc-country-select"
            >
              <option value="">{t("calc.pickCountry")}</option>
              {countries.map((c) => (
                <option key={c.country_iso2} value={c.country_iso2}>{flag(c.country_iso2)} {countryName(c.country_iso2, i18n.language, c.country)}</option>
              ))}
            </select>
          </label>
          <button onClick={() => country && setStep(1)} disabled={!country} className="btn-primary mt-6 w-full" data-testid="calc-next-0">
            {t("common.continue")} <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      )}

      {/* STEP 1 — Trip length */}
      {step === 1 && (
        <div data-testid="calc-step-trip">
          <label className="block mb-3 text-sm font-semibold">{t("calc.q2")}</label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {TRIP_LENGTHS.map((tt) => (
              <button
                key={tt.id}
                onClick={() => setTrip(tt)}
                className={`px-4 py-3 rounded-2xl font-semibold border transition ${trip?.id === tt.id ? "border-cobalt-500 bg-cobalt-50 text-cobalt-600" : "border-ink-200 hover:border-cobalt-300"}`}
                data-testid={`calc-trip-${tt.id}`}
              >
                {t(tt.label)}
              </button>
            ))}
          </div>
          <div className="mt-6 flex gap-2">
            <button onClick={() => setStep(0)} className="btn-outline flex-1">{t("common.back")}</button>
            <button onClick={() => trip && setStep(2)} disabled={!trip} className="btn-primary flex-1" data-testid="calc-next-1">
              {t("common.continue")}
            </button>
          </div>
        </div>
      )}

      {/* STEP 2 — Activities */}
      {step === 2 && (
        <div data-testid="calc-step-acts">
          <label className="block mb-3 text-sm font-semibold">{t("calc.q3")}</label>
          <div className="grid grid-cols-2 gap-3">
            {ACTIVITIES.map((a) => {
              const Icon = a.icon;
              const on = !!acts[a.id];
              return (
                <button
                  key={a.id}
                  onClick={() => toggleAct(a.id)}
                  className={`p-4 rounded-2xl border text-left transition ${on ? "border-cobalt-500 bg-cobalt-50/40" : "border-ink-200 hover:border-cobalt-300"}`}
                  data-testid={`calc-act-${a.id}`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-2 ${a.color}`}>
                    <Icon size={20} weight="bold" />
                  </div>
                  <div className="font-semibold">{t(a.label)}</div>
                  <div className="text-xs text-ink-500 mt-0.5">{a.emoji} ~{Math.round(a.mbPerDay/1024*10)/10} GB/day</div>
                </button>
              );
            })}
          </div>
          <div className="mt-6 flex gap-2">
            <button onClick={() => setStep(1)} className="btn-outline flex-1">{t("common.back")}</button>
            <button onClick={() => Object.values(acts).some(Boolean) && setStep(3)} disabled={!Object.values(acts).some(Boolean)} className="btn-primary flex-1" data-testid="calc-next-2">
              {t("calc.seeResult")}
            </button>
          </div>
        </div>
      )}

      {/* STEP 3 — Result */}
      {step === 3 && (
        <div data-testid="calc-step-result">
          <div className="text-center py-4">
            <div className="overline mb-2">{t("calc.recommends")}</div>
            <div className="font-display font-bold text-7xl tracking-tight text-cobalt-500">{recommendedGb}<span className="text-3xl text-ink-700"> GB</span></div>
            <p className="mt-3 text-ink-700">{t("calc.estimateNote")}</p>
            <div className="mt-4 inline-flex items-center gap-2 pill bg-cobalt-50 text-cobalt-600">
              <Lightning size={14} weight="fill" /> {countryName(country?.country_iso2, i18n.language, country?.country)} · {trip?.id} {t("calc.daysShort")}
            </div>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-2">
            <button onClick={reset} className="btn-outline">{t("calc.startOver")}</button>
            <button onClick={goPlans} className="btn-primary" data-testid="calc-cta">{t("calc.findPlan")} <ArrowRight size={16} weight="bold" /></button>
          </div>
        </div>
      )}
    </Container>
  );
}
